import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, RefreshCcw } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { MantenedoresHeader } from './components/MantenedoresHeader';
import { MantenedoresTabs } from './components/MantenedoresTabs';
import { MantenedoresFilter } from './components/MantenedoresFilter';
import { MantenedoresTable } from './components/MantenedoresTable';
import { MantenedorFormModal } from './components/MantenedorFormModal';
import type { MantenedorRecord, MantenedorTab } from './types';
import { FacturacionSiatTab } from './components/FacturacionSiatTab';

export const MantenedoresPage = () => {
    const [activeTab, setActiveTab] = useState<MantenedorTab>('propiedad');
    const [searchQuery, setSearchQuery] = useState('');
    const [records, setRecords] = useState<MantenedorRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<MantenedorRecord | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [recordToToggle, setRecordToToggle] = useState<MantenedorRecord | null>(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    useEffect(() => {
        if (activeTab === 'facturacion') {
            setRecords([]); // Limpiamos la tabla
            return;
        }

        const fetchMantenedores = async () => {
            setIsLoading(true);
            try {
                let endpoint = '';

                if (activeTab === 'alojamiento') {
                    endpoint = 'Parametrics/AccomodationTypes?includeInactive=true';
                } else {
                    endpoint = 'Parametrics/PropertyTypes?includeInactive=true';
                }

                const response = await apiClient.get<any>(endpoint);
                let resBody = response;

                if (response.data && response.data.statusCode !== undefined) {
                    resBody = response.data;
                }

                if (resBody.hasError) {
                    throw new Error(resBody.resultError?.message || "Error al cargar los datos");
                }

                const rawData = Array.isArray(resBody.data) ? resBody.data : [];

                // Mapeo de datos dependiendo del endpoint
                const mappedData: MantenedorRecord[] = rawData.map((item: any) => {
                    if (activeTab === 'alojamiento') {
                        return {
                            // Mapeo para tipo de alojamiento
                            id: item.accommodationTypeId,
                            code: item.accomodationCode,
                            name: item.accommodationName,
                            description: item.accommodationDescription || 'Sin descripción',
                            icon: item.accommodationIcon,
                            status: item.status
                        };
                    } else {
                        return {
                            // Mapeo para tipo de propiedad
                            id: item.propertySubtypeId,
                            propertyTypeId: item.propertyTypeId,
                            code: item.subTypeCode,
                            name: item.subType,
                            description: item.description || 'Sin descripción',
                            icon: item.subTypeIcon,
                            status: item.status
                        };
                    }
                });

                setRecords(mappedData);
            } catch (error) {
                console.error("Error fetching mantenedores:", error);
                setRecords([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMantenedores();
    }, [activeTab, refreshTrigger]);

    const filteredData = records.filter(record =>
        (record.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (record.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (record.code || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleTabChange = (tab: MantenedorTab) => {
        setActiveTab(tab);
        setSearchQuery('');
    };

    const handleOpenCreate = () => {
        setEditingRecord(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (record: MantenedorRecord) => {
        setEditingRecord(record);
        setIsModalOpen(true);
    };

    const handleToggleClick = (record: MantenedorRecord) => {
        setRecordToToggle(record);
    };

    // Método para cambiar el estado
    const confirmStatusChange = async () => {
        if (!recordToToggle) return;

        try {
            setIsUpdatingStatus(true);

            const targetStatus = recordToToggle.status === 1 ? 0 : 1;

            if (activeTab === 'propiedad') {
                const response = await apiClient.put<any>(`Parametrics/PropertyTypes/${recordToToggle.id}/Status/${targetStatus}`);

                if (response.data && response.data.hasError) {
                    throw new Error(response.data.resultError?.message || "Error al cambiar el estado de la propiedad.");
                }
            } else {
                // Cambio de estado para alojamiento
                const response = await apiClient.put<any>(`Parametrics/AccomodationTypes/${recordToToggle.id}/Status/${targetStatus}`);

                if (response.data && response.data.hasError) {
                    throw new Error(response.data.resultError?.message || "Error al cambiar el estado del alojamiento.");
                }
            }

            setRecordToToggle(null);
            setRefreshTrigger(prev => prev + 1);

        } catch (error) {
            console.error("Error al cambiar el estado:", error);
            alert("Hubo un error al intentar cambiar el estado del registro.");
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // Función que recibe los datos del modal
    const handleSaveRecord = async (formData: any) => {
        try {
            setIsSaving(true);

            if (!editingRecord) {
                // LÓGICA DE CREACIÓN (POST)
                if (activeTab === 'propiedad') {
                    // JSON
                    const payload = {
                        propertyTypeId: Number(formData.propertyTypeId),
                        code: formData.code.toUpperCase(),
                        name: formData.name,
                        icon: formData.icon,
                        description: formData.description
                    };

                    const response = await apiClient.post<any>('Parametrics/PropertyTypes', payload);

                    if (response.data && response.data.hasError) {
                        throw new Error(response.data.resultError?.message || "Error al crear la propiedad");
                    }
                } else {
                    // Lógica para alojamiento
                    // JSON
                    const payload = {
                        accommodationName: formData.name,
                        accommodationDescription: formData.description,
                        accomodationCode: formData.code.toUpperCase(),
                        accommodationIcon: formData.icon,
                    };

                    const response = await apiClient.post<any>('Parametrics/AccomodationTypes', payload);

                    if (response.data && response.data.hasError) {
                        throw new Error(response.data.resultError?.message || "Error al crear el alojamiento.");
                    }
                }
            } else {
                // LÓGICA DE EDICIÓN (PUT)
                if (activeTab === 'propiedad') {
                    const payload = {
                        PropertyTypeId: Number(formData.propertyTypeId),
                        Code: formData.code.toUpperCase(),
                        Name: formData.name,
                        Icon: formData.icon,
                        Description: formData.description,
                    };

                    const response = await apiClient.put<any>(`Parametrics/PropertyTypes/${editingRecord.id}`, payload);

                    if (response.data && response.data.hasError) {
                        throw new Error(response.data.resultError?.message || "Error al actualizar la propiedad");
                    }
                } else {
                    // Lógica de edición para 'alojamiento'
                    const payload = {
                        accommodationName: formData.name,
                        accommodationDescription: formData.description,
                        accomodationCode: formData.code.toUpperCase(),
                        accommodationIcon: formData.icon,
                    };

                    const response = await apiClient.put<any>(`Parametrics/AccomodationTypes/${editingRecord.id}`, payload);

                    if (response.data && response.data.hasError) {
                        throw new Error(response.data.resultError?.message || "Error al actualizar el alojamiento.");
                    }
                }
            }

            // Se cierra el modal y recarga la tabla
            setIsModalOpen(false);
            setRefreshTrigger(prev => prev + 1);

        } catch (error) {
            console.error("Error guardando el registro:", error);
            alert("Hubo un error al guardar. Revisa la consola para más detalles.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="relative flex flex-col w-full h-screen overflow-hidden font-sans text-gray-900 bg-gray-50">
            <div className="flex flex-col flex-1 w-full h-full overflow-y-auto overflow-x-hidden hide-scrollbar bg-white">

                <MantenedoresHeader />

                <MantenedoresTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                {activeTab !== 'facturacion' && (
                    <MantenedoresFilter
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        activeTab={activeTab}
                        onCreateClick={handleOpenCreate}
                    />
                )}

                <div className="flex-1 flex flex-col relative min-h-[400px]">
                    {activeTab === 'facturacion' ? (
                        <FacturacionSiatTab />
                    ) : (
                        <>
                            {isLoading && (
                                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px]">
                                    <Loader2 className="w-8 h-8 animate-spin text-[#4f6df5] mb-2" />
                                    <span className="text-xs font-semibold text-gray-500">Cargando registros...</span>
                                </div>
                            )}

                            <MantenedoresTable
                                records={filteredData}
                                onEdit={handleOpenEdit}
                                onToggleStatus={handleToggleClick}
                                showIconColumn={true}
                                showDescriptionColumn={true}
                            />
                        </>
                    )}
                </div>

            </div>

            {activeTab !== 'facturacion' && (
                <MantenedorFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    editingRecord={editingRecord}
                    activeTab={activeTab}
                    onSave={handleSaveRecord}
                    isSaving={isSaving}
                />
            )}

            {/* MODAL DE ESTADO (ACTIVAR / DESACTIVAR) */}
            {recordToToggle && activeTab !== 'facturacion' && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-[400px] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-6 text-center sm:p-7">
                            <div className={`flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full ${recordToToggle.status === 1 ? 'bg-red-100' : 'bg-emerald-100'
                                }`}>
                                {recordToToggle.status === 1 ? (
                                    <AlertTriangle className="text-red-600" size={24} />
                                ) : (
                                    <RefreshCcw className="text-emerald-600" size={24} />
                                )}
                            </div>
                            <h3 className="mb-2 text-lg font-bold text-gray-900">
                                {recordToToggle.status === 1 ? '¿Desactivar registro?' : '¿Activar registro?'}
                            </h3>
                            <p className="text-[13px] text-gray-600 leading-relaxed">
                                {recordToToggle.status === 1
                                    ? 'Esta acción cambiará el estado del registro a inactivo y dejará de estar disponible en las opciones principales.'
                                    : 'Esta acción volverá a activar el registro y lo hará visible nuevamente en las opciones del sistema.'}
                            </p>
                        </div>

                        <div className="flex items-center justify-center gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <button
                                onClick={() => setRecordToToggle(null)}
                                disabled={isUpdatingStatus}
                                className="w-full px-4 py-2 text-[13px] font-semibold text-gray-600 transition-colors bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmStatusChange}
                                disabled={isUpdatingStatus}
                                className={`flex items-center justify-center w-full gap-2 px-4 py-2 text-[13px] font-semibold text-white transition-colors rounded-lg disabled:opacity-50 ${recordToToggle.status === 1
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : 'bg-emerald-600 hover:bg-emerald-700'
                                    }`}
                            >
                                {isUpdatingStatus
                                    ? 'Procesando...'
                                    : (recordToToggle.status === 1 ? 'Sí, desactivar' : 'Sí, activar')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default MantenedoresPage;